import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Plus, Edit2, Trash2, Save, X, ArrowLeft, ExternalLink } from 'lucide-react';
import api from '../services/api';

const VideoTutorialsManagementPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    youtube_url: '',
    description: '',
    category: 'General',
    order: 1
  });

  const categories = ['General', 'Inicio', 'Hotspot', 'PPPoE', 'VPN', 'Herramientas', 'Avanzado'];

  useEffect(() => {
    loadTutorials();
  }, []);

  const loadTutorials = async () => {
    try {
      const response = await api.get('/api/superadmin/tutorials');
      setTutorials(response.data.tutorials || []);
    } catch (error) {
      console.error('Error:', error);
      alert('Error cargando tutoriales');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.youtube_url) {
      alert('Título y URL son obligatorios');
      return;
    }

    setProcessing(true);
    try {
      if (editingId) {
        await api.put(`/api/superadmin/tutorials/${editingId}`, formData);
        alert('✅ Tutorial actualizado');
      } else {
        await api.post('/api/superadmin/tutorials', formData);
        alert('✅ Tutorial creado');
      }
      
      resetForm();
      await loadTutorials();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error guardando tutorial');
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (tutorial) => {
    setFormData({
      title: tutorial.title,
      youtube_url: tutorial.youtube_url,
      description: tutorial.description || '',
      category: tutorial.category || 'General',
      order: tutorial.order || 1
    });
    setEditingId(tutorial.id);
    setShowForm(true);
  };

  const handleDelete = async (id, title) => {
    if (!confirm(`¿Eliminar tutorial "${title}"?`)) return;

    setProcessing(true);
    try {
      await api.delete(`/api/superadmin/tutorials/${id}`);
      alert('✅ Tutorial eliminado');
      await loadTutorials();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error eliminando tutorial');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      youtube_url: '',
      description: '',
      category: 'General',
      order: 1
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b shadow-sm`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/super-admin')}
              className="p-2 hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                📺 Gestión de Videotutoriales
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Administra los videos que se muestran en /tutoriales
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold"
          >
            <Plus className="w-5 h-5" />
            Agregar Video
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Lista de videos */}
        <div className="space-y-4">
          {tutorials.map((tutorial) => {
            const videoId = getYouTubeId(tutorial.youtube_url);
            const thumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            
            return (
              <div
                key={tutorial.id}
                className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-4 flex gap-4`}
              >
                {/* Thumbnail */}
                <div className="w-48 flex-shrink-0">
                  <img
                    src={thumbnail}
                    alt={tutorial.title}
                    className="w-full rounded-lg"
                  />
                </div>

                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {tutorial.title}
                      </h3>
                      <span className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded-full mt-1">
                        {tutorial.category}
                      </span>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(tutorial)}
                        className="p-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tutorial.id, tutorial.title)}
                        disabled={processing}
                        className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {tutorial.description && (
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                      {tutorial.description}
                    </p>
                  )}
                  
                  <a
                    href={tutorial.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 text-sm flex items-center gap-2 hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver en YouTube
                  </a>
                  
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-2`}>
                    Orden: {tutorial.order}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {tutorials.length === 0 && (
          <div className="text-center py-12">
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-lg mb-4`}>
              No hay videotutoriales creados
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
            >
              Agregar primer video
            </button>
          </div>
        )}
      </main>

      {/* Modal Formulario */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className="bg-gradient-to-r from-blue-600 to-purple-700 p-6 text-white rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">
                  {editingId ? '✏️ Editar Video' : '➕ Agregar Video'}
                </h2>
                <button onClick={resetForm} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Título */}
              <div>
                <label className={`block mb-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Ej: Cómo configurar Hotspot"
                  required
                />
              </div>

              {/* URL de YouTube */}
              <div>
                <label className={`block mb-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  URL de YouTube *
                </label>
                <input
                  type="url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
              </div>

              {/* Descripción */}
              <div>
                <label className={`block mb-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Breve descripción del video"
                  rows="3"
                />
              </div>

              {/* Categoría */}
              <div>
                <label className={`block mb-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Categoría
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Orden */}
              <div>
                <label className={`block mb-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Orden de aparición
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className={`w-full px-4 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  min="1"
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Los videos se ordenan de menor a mayor
                </p>
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className={`flex-1 px-4 py-3 rounded-lg font-semibold ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                  disabled={processing}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                  disabled={processing}
                >
                  <Save className="w-5 h-5" />
                  {editingId ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoTutorialsManagementPage;
